import { useState } from 'react';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Button } from './ui/button';

import type { CreateQuestionRequest } from '../types/Question.api.type';

interface DurationStepProps {
  handlePrev: () => void;
  handleSubmit: () => void;
  createQuestionParam: CreateQuestionRequest;
  setCreateQuestionParamHandler: (key: keyof CreateQuestionRequest, value: string | string[] | number | boolean) => void;
  submitButtonText?: string;
}

const DURATION_OPTIONS = [
  { value: '1day', label: '1일', hours: 24 },
  { value: '3days', label: '3일', hours: 72 },
  { value: '1week', label: '1주일', hours: 168 },
  { value: '2weeks', label: '2주일', hours: 336 },
];

const DurationStep = ({
  handlePrev,
  handleSubmit,
  createQuestionParam,
  setCreateQuestionParamHandler,
  submitButtonText = '등록',
}: DurationStepProps) => {
  const [selectDurationValue, setSelectDurationValue] = useState<string | null>(null);

  const handleDurationSelect = (duration: { value: string; hours: number }) => {
    setSelectDurationValue(duration.value);
    setCreateQuestionParamHandler('presetDuration', duration.hours);
  };

  const getEndDate = () => {
    if (createQuestionParam.presetDuration <= 0) return '기간을 선택해주세요';
    const endDate = new Date(new Date().getTime() + createQuestionParam.presetDuration * 60 * 60 * 1000);
    return `설문종료: ${endDate.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-3">설문 기간</h3>
        <div className="grid grid-cols-2 gap-3">
          {DURATION_OPTIONS.map((option) => (
            <Card
              key={option.value}
              className={`
                py-3 px-3 cursor-pointer text-center transition-all
                border-2
                ${
                  selectDurationValue === option.value
                    ? 'border-primary bg-primary/10 ring-2 ring-primary/30 shadow-md'
                    : 'border-transparent hover:border-gray-300'
                }
              `}
              onClick={() => handleDurationSelect(option)}
            >
              <div className="font-medium text-sm">{option.label}</div>
              <div className="text-xs text-muted-foreground">{option.hours}시간</div>
            </Card>
          ))}
        </div>

      </div>

      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">{getEndDate()}</span>
        </div>
      </div>

      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div>
          <Label htmlFor="member-only" className="font-medium">
            회원 전용 설문
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            로그인한 사용자만 참여할 수 있습니다
          </p>
        </div>
        <Switch
          id="member-only"
          checked={createQuestionParam.memberOnly}
          onCheckedChange={(checked) => setCreateQuestionParamHandler('memberOnly', checked)}
        />
      </div>

      {/* 버튼 */}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={handlePrev}>
          뒤로
        </Button>
        <Button className="flex-1" disabled={!(createQuestionParam.presetDuration > 0)} onClick={handleSubmit}>
          {submitButtonText}
        </Button>
      </div>
    </div>
  );
};

export default DurationStep;
